/**
 * Chat Component
 * Full-featured chat with messages, voice recording, file uploads
 */
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axiosClient from '../../utils/axiosClient';
import axios from 'axios'; // Behalte für FormData-Uploads
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Send, Paperclip, Image, FileText, Download, X, Mic, Square, MessageCircle, ArrowLeft, Settings, Ruler } from 'lucide-react';
import { API, getApiUrl } from '../../utils/api';
import { getFullFileUrl } from '../../utils/fileUtils';
import { DebugUrlDisplay } from '../debug';
import { LANGUAGES } from '../../constants/languages';

const Chat = ({ project, currentUser, onFilePreview, scrollToMessage, scrollBehavior, onScrollComplete }) => {
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [pendingPhoto, setPendingPhoto] = useState(null); // For photo + caption workflow
  const [pendingTranscript, setPendingTranscript] = useState(null); // For transcript review workflow
  
  // NEUER STATE: Speichert welche Übersetzungen ausgeklappt sind (persistiert über Auto-Refresh)
  const [expandedTranslations, setExpandedTranslations] = useState(new Set());
  
  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false); // NEW: Loading indicator for transcription
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioChunks, setAudioChunks] = useState([]);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordingTimer, setRecordingTimer] = useState(null);
  const [projectMembers, setProjectMembers] = useState({});
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const messagesRef = useRef(messages); // Ref to always have the latest messages
  
  // Mobile detection hook
  const [isMobile, setIsMobile] = useState(false);
  
  // Keep messagesRef in sync with messages state
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);
  
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const formatMessageTime = (timestamp) => {
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      
      const now = new Date();
      const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
      
      const locale = currentUser?.language || 'de';
      const timeStr = date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      const dateStr = date.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: '2-digit' });
      
      if (diffDays === 0) {
        return `${timeStr} (${t('chat.today')})`;
      } else if (diffDays === 1) {
        return `${timeStr} (${t('chat.yesterday')})`;
      } else {
        return `${timeStr} (${dateStr})`;
      }
    } catch (error) {
      console.error('Error formatting message time:', error);
      return 'Invalid Date';
    }
  };

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      // Scroll only the chat container, not the whole page
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  };

  const scrollToSpecificMessage = (messageId) => {
    if (chatContainerRef.current && messageId) {
      // Try to find the message element with retries for delayed loading
      const findAndScrollToMessage = (retries = 5) => {
        const messageElement = document.querySelector(`[data-message-id="${messageId}"]`);
        if (messageElement) {
          console.log('✅ Found message element, scrolling to message:', messageId);
          messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Highlight message briefly
          messageElement.style.backgroundColor = '#fef3c7';
          setTimeout(() => {
            messageElement.style.backgroundColor = 'transparent';
          }, 2000);
          return true;
        } else if (retries > 0) {
          console.log(`⏳ Message element not found, retrying... (${retries} attempts left)`);
          setTimeout(() => findAndScrollToMessage(retries - 1), 200);
        } else {
          console.error('❌ Message element not found after all retries:', {
            messageId,
            availableMessages: document.querySelectorAll('[data-message-id]').length,
            messagesWithIds: Array.from(document.querySelectorAll('[data-message-id]')).map(el => el.getAttribute('data-message-id'))
          });
        }
        return false;
      };
      
      findAndScrollToMessage();
    }
  };

  // Handle initial scroll behavior - only on specific triggers, not every message change
  useEffect(() => {
    // DISABLED: No automatic scrolling to specific messages
    // if (scrollToMessage && messages.length > 0) {
    //   console.log('🎯 Scrolling to specific message:', scrollToMessage);
    //   setTimeout(() => {
    //     scrollToSpecificMessage(scrollToMessage);
    //     if (onScrollComplete) onScrollComplete();
    //   }, 500);
    // }
  }, [scrollToMessage]); // Auto-scroll completely disabled

  // Handle bottom scroll only when explicitly requested
  useEffect(() => {
    // DISABLED: No automatic scrolling at all
    // if (messages.length > 0 && scrollBehavior === 'bottom' && scrollToMessage === null) {
    //   scrollToBottom();
    //   if (onScrollComplete) onScrollComplete();
    // }
    // Auto-scroll completely disabled for "Alle Nachrichten" navigation
  }, [scrollBehavior]); // Only trigger when scrollBehavior changes

  // Auto-refresh but don't auto-scroll unless it's the first load
  useEffect(() => {
    if (project) {
      loadMessages();
      loadProjectMembers();
      
      // CRITICAL FIX: Reset scroll position to top when project changes
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop = 0;
      }
      
      // Auto-refresh messages every 30 seconds but don't auto-scroll
      const messagesInterval = setInterval(() => {
        // Always refresh but preserve translations via loadMessages logic
        loadMessages(false); // Pass false to indicate this is not initial load
      }, 30000);
      
      console.log('Started auto-refresh for messages');
      
      return () => {
        clearInterval(messagesInterval);
      };
    }
  }, [project?.id]); // Use project.id instead of project object

  const loadProjectMembers = async () => {
    try {
      const response = await axiosClient.get(`/projects/${project.id}/members`);
      const membersMap = {};
      response.data.forEach(member => {
        membersMap[member.id] = member.name;
      });
      setProjectMembers(membersMap);
    } catch (error) {
      // Stille Fehler bei temporären Server-Problemen
      if (!error.response || ![500, 502, 520].includes(error.response?.status)) {
        console.error('Error loading project members:', error);
      }
    }
  };

  const loadMessages = async (isInitialLoad = true) => {
    // Only show loading spinner on initial load, not on background refresh
    if (isInitialLoad && !initialLoadDone) {
      setLoading(true);
    }
    try {
      const response = await axiosClient.get(`/messages/${project.id}`, {
        params: { user_language: currentUser?.language || 'de' }
      });
      
      setInitialLoadDone(true);
      
      // TRANSLATION PERSISTENCE FIX: Preserve existing translations during auto-refresh
      const newMessages = response.data;
      
      // If this is not the initial load (auto-refresh), preserve existing translations AND visibility state
      if (!isInitialLoad && messages.length > 0) {
        // Create a map of existing translations (prioritize frontend translations)
        // Use messagesRef.current to get the LATEST messages state (avoid stale closure)
        const existingStates = {};
        const currentMessages = messagesRef.current || [];
        
        currentMessages.forEach(msg => {
          // Capture translation state AND visibility state
          existingStates[msg.id] = {
            translated_text: msg.translated_text,
            has_translation: msg.has_translation,
            _translation_permanent: msg._translation_permanent || false,
            _showTranslation: msg._showTranslation || false // WICHTIG: Sichtbarkeitsstatus erhalten!
          };
        });
        
        // Apply existing states to new messages
        newMessages.forEach(msg => {
          const existingState = existingStates[msg.id];
          if (existingState) {
            // Preserve visibility state
            msg._showTranslation = existingState._showTranslation;
            
            // If we have a permanent frontend translation, use it over backend
            if (existingState._translation_permanent) {
              msg.translated_text = existingState.translated_text;
              msg.has_translation = existingState.has_translation;
              msg._translation_permanent = true;
            } else if (!msg.has_translation || !msg.translated_text) {
              // Only use existing if backend doesn't have translation
              if (existingState.translated_text) {
                msg.translated_text = existingState.translated_text;
                msg.has_translation = existingState.has_translation;
              }
            }
            
            // If message has translation (from backend or local), keep show state
            if (msg.has_translation && msg.translated_text) {
              msg._showTranslation = existingState._showTranslation;
            }
          }
        });
      }
      
      // Backend sends newest first - perfect for timeline view (newest at top)
      // PERSISTENT TRANSLATION FIX: Ensure translations are preserved across reloads
      const messagesWithPersistentTranslations = newMessages.map(msg => {
        // Force has_translation to true if translated_text exists (defensive programming)
        if (msg.translated_text && !msg.has_translation) {
          msg.has_translation = true;
        }
        return msg;
      });
      
      setMessages(messagesWithPersistentTranslations);
      
    } catch (error) {
      // Stille Fehler bei temporären Server-Problemen - Daten bleiben erhalten
      if (!error.response || ![500, 502, 520].includes(error.response?.status)) {
        console.error('Error loading messages:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    
    setLoading(true);
    const messageText = newMessage;
    setNewMessage(''); // Clear input immediately
    
    try {
      if (editingMessage) {
        // Edit existing message
        const response = await axios.put(`${getApiUrl()}/messages/${editingMessage.id}?user_id=${currentUser.id}`, {
          original_text: messageText
        });
        
        setMessages(prev => prev.map(msg => 
          msg.id === editingMessage.id ? response.data : msg
        ));
        setEditingMessage(null);
        
      } else {
        // Send new message
        const messageData = {
          project_id: project.id,
          original_text: messageText,
          message_type: "text"
        };
        
        if (replyingTo) {
          messageData.reply_to = replyingTo.id;
          messageData.reply_to_text = replyingTo.original_text;
        }
        
        const response = await axios.post(`${getApiUrl()}/messages?sender_id=${currentUser.id}`, messageData);
        
        // Add message to local state instead of reloading all
        // Add new message at the TOP for newest-first sorting (NEWEST OBEN)
        setMessages(prev => [response.data, ...prev]);
        setReplyingTo(null);
        
        // No automatic scrolling - user can scroll manually
      }
      
    } catch (error) {
      console.error('Error sending message:', error);
      setNewMessage(messageText); // Restore message on error
      alert(t('errors.sendMessage'));
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${getApiUrl()}/upload/chat?project_id=${project.id}&sender_id=${currentUser.id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          }
        }
      );

      // Reload messages to show the uploaded file
      await loadMessages();
      
    } catch (error) {
      console.error('Error uploading file:', error);
      alert(t('chat.fileUploadError', 'Error uploading file: ') + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  // Special handler for photo capture with caption workflow
  const handlePhotoCapture = async (event) => {
    console.log('📷 Camera button clicked!', event);
    const file = event.target.files[0];
    console.log('📷 File selected:', file);
    
    if (!file) {
      console.log('📷 No file selected');
      return;
    }

    // Check if it's actually an image
    if (!file.type.startsWith('image/')) {
      alert('Bitte wählen Sie ein Bild aus.');
      console.log('📷 Invalid file type:', file.type);
      return;
    }

    console.log('📷 Valid image file, creating preview');
    // Store photo temporarily and ask for caption
    setPendingPhoto({
      file: file,
      preview: URL.createObjectURL(file)
    });
    
    // Reset file input
    event.target.value = '';
    console.log('📷 Photo capture complete');
  };

  // Direct click handler for camera button
  const handleCameraClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    console.log('📷 Camera button clicked directly!', event.type);
    
    const fileInput = document.getElementById('camera-file-input');
    if (fileInput) {
      console.log('📷 Triggering file input click');
      fileInput.click();
    } else {
      console.error('📷 Camera file input not found!');
    }
  };

  // Upload photo with caption
  const uploadPhotoWithCaption = async (caption) => {
    if (!pendingPhoto) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', pendingPhoto.file);
      
      // Add caption as form data if provided
      if (caption.trim()) {
        formData.append('caption', caption.trim());
      }

      const response = await axios.post(
        `${getApiUrl()}/upload/chat?project_id=${project.id}&sender_id=${currentUser.id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          }
        }
      );

      // Reload messages to show the uploaded photo with caption
      await loadMessages();
      
      // No auto-scroll - let user see messages naturally
      // setTimeout(scrollToBottom, 100); // REMOVED - forced scroll
      
    } catch (error) {
      console.error('Error uploading photo:', error);
      alert(t('chat.photoUploadError', 'Error uploading photo: ') + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
      // Clear pending photo - WICHTIG: Prüfen ob noch vorhanden (Race Condition Schutz)
      setPendingPhoto(prev => {
        if (prev && prev.preview) {
          try {
            URL.revokeObjectURL(prev.preview);
          } catch (e) {
            console.warn('Could not revoke object URL:', e);
          }
        }
        return null;
      });
    }
  };

  // Voice Recording Functions with Web Speech API - SIMPLIFIED VERSION
  const startVoiceRecording = async () => {
    console.log('🎤 SIMPLE AUDIO - Starting recording (no transcription)');
    console.log('  Current User:', currentUser);
    
    try {
      // Reset state
      setIsRecording(true);
      setRecordingTime(0);
      
      // Get microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      console.log('🎤 SIMPLE AUDIO - Microphone access granted');
      
      // Setup MediaRecorder
      const recorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
      });
      
      const chunks = [];
      
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };
      
      recorder.onstop = () => {
        const audioBlob = new Blob(chunks, { type: recorder.mimeType });
        
        console.log('🎤 SIMPLE AUDIO - Recording stopped, blob size:', audioBlob.size);
        
        // Send audio file (no transcript)
        sendVoiceMessage(audioBlob, '');
        
        // Stop all tracks to release microphone
        stream.getTracks().forEach(track => track.stop());
      };
      
      setMediaRecorder(recorder);
      setAudioChunks(chunks);
      
      // Start recording timer
      const timer = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      setRecordingTimer(timer);
      
      recorder.start();
      console.log('🎤 SIMPLE AUDIO - Recording started successfully');
      
    } catch (error) {
      console.error('🎤 SIMPLE AUDIO - Error starting recording:', error);
      setIsRecording(false);
      alert(t('chat.microphoneError', 'Error accessing microphone'));
    }
  };

  const stopVoiceRecording = () => {
    if (mediaRecorder && mediaRecorder.state === 'recording') {
      mediaRecorder.stop();
      setIsRecording(false);
      
      // Clear timer
      if (recordingTimer) {
        clearInterval(recordingTimer);
        setRecordingTimer(null);
      }
      
      console.log('🎤 SIMPLE AUDIO - Voice recording stopped');
    }
  };

  const sendVoiceMessage = async (audioBlob, transcript = '') => {
    console.log('🎤 SERVER TRANSCRIPTION - Sending voice message with auto-transcription:', {
      audioBlobSize: audioBlob.size,
      userLanguage: currentUser?.language || 'de'
    });
    
    setUploading(true);
    setIsTranscribing(true); // Show transcription loading indicator
    try {
      // Step 1: Upload the voice message audio file
      const formData = new FormData();
      formData.append('file', audioBlob, 'voice_message.webm');
      formData.append('caption', '🎤 Sprachnachricht');

      const voiceResponse = await axios.post(
        `${getApiUrl()}/upload/chat?project_id=${project.id}&sender_id=${currentUser.id}`, 
        formData, 
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          }
        }
      );

      console.log('✅ Voice message uploaded:', voiceResponse.data);
      
      // Step 2: Request automatic server-side transcription
      const uploadedFilePath = voiceResponse.data.file_path;
      const voiceMessageId = voiceResponse.data.message_id;
      
      try {
        console.log('🎤 SERVER TRANSCRIPTION - Requesting transcription for:', uploadedFilePath);
        
        const transcriptionResponse = await axios.post(`${getApiUrl()}/transcribe-voice`, {
          project_id: project.id,
          sender_id: currentUser.id,
          file_path: uploadedFilePath,
          language: currentUser.language || 'de'
        });
        
        console.log('✅ Server transcription completed:', transcriptionResponse.data);
        
        // Step 3: Show transcript for review/editing before saving
        setPendingTranscript({
          text: transcriptionResponse.data.transcript_text || '',
          voiceMessageId: voiceMessageId,
          transcriptMessageId: transcriptionResponse.data.transcript_message_id,
          filePath: uploadedFilePath
        });
        
      } catch (transcriptionError) {
        console.error('❌ Server transcription failed:', transcriptionError);
        
        // Show empty transcript for manual input
        setPendingTranscript({
          text: '',
          voiceMessageId: voiceMessageId,
          transcriptMessageId: null,
          filePath: uploadedFilePath,
          error: t('chat.transcriptionFailed', 'Automatic transcription failed. Please enter text manually.')
        });
      }
      
    } catch (error) {
      console.error('❌ Voice message upload failed:', error);
      alert(t('chat.voiceMessageError', 'Error sending voice message'));
    } finally {
      setUploading(false);
      setIsTranscribing(false); // Hide transcription loading indicator
    }
  };

  // Save the reviewed/edited transcript
  const saveReviewedTranscript = async (editedText) => {
    if (!pendingTranscript) return;
    
    try {
      if (pendingTranscript.transcriptMessageId) {
        // Update existing transcript message - use PUT with user_id and 'text' field
        await axios.put(`${getApiUrl()}/messages/${pendingTranscript.transcriptMessageId}?user_id=${currentUser.id}`, {
          text: editedText
        });
      } else {
        // Create new transcript message (if auto-transcription failed)
        await axios.post(`${getApiUrl()}/messages?sender_id=${currentUser.id}`, {
          project_id: project.id,
          original_text: editedText || '📝 (Keine Beschreibung)',
          message_type: 'text'
        });
      }
      
      // Reload messages to show the updated transcript
      loadMessages();
      setPendingTranscript(null);
      
    } catch (error) {
      console.error('Error saving transcript:', error);
      alert(t('errors.saveTranscript', 'Fehler beim Speichern des Transkripts'));
    }
  };

  // Cancel transcript and delete the voice message
  const cancelTranscript = async () => {
    if (pendingTranscript?.voiceMessageId) {
      try {
        // Optionally delete the voice message if user cancels
        // await axios.delete(`${getApiUrl()}/messages/${pendingTranscript.voiceMessageId}`);
      } catch (error) {
        console.error('Error canceling:', error);
      }
    }
    setPendingTranscript(null);
  };



  const handleReplyToMessage = (message) => {
    setReplyingTo(message);
  };

  const handleEditMessage = (message) => {
    if (message.message_type !== 'text' || !message.original_text) {
      alert('Nur Textnachrichten können bearbeitet werden.');
      return;
    }
    
    const newText = prompt('Nachricht bearbeiten:', message.original_text);
    if (newText && newText !== message.original_text) {
      editMessage(message.id, newText);
    }
  };

  const handleDeleteMessage = (messageId) => {
    if (confirm(t('chat.confirmDelete', 'Sind Sie sicher, dass Sie diese Nachricht löschen möchten?'))) {
      deleteMessage(messageId);
    }
  };

  const editMessage = async (messageId, newText) => {
    try {
      await axios.put(`${getApiUrl()}/messages/${messageId}?user_id=${currentUser.id}`, {
        text: newText
      });
      
      // Refresh messages to show the edit
      loadMessages();
    } catch (error) {
      console.error('Error editing message:', error);
      alert(t('chat.editError', 'Fehler beim Bearbeiten der Nachricht') + ': ' + (error.response?.data?.detail || error.message));
    }
  };

  const deleteMessage = async (messageId) => {
    try {
      await axios.delete(`${getApiUrl()}/messages/${messageId}?user_id=${currentUser.id}`);
      
      // Refresh messages to remove the deleted message
      loadMessages();
    } catch (error) {
      console.error('Error deleting message:', error);
      alert(t('chat.deleteMessageError', 'Error deleting message: ') + (error.response?.data?.detail || error.message));
    }
  };

  const translateMessage = async (messageId, targetLanguage) => {
    try {
      console.log('🌐 TRANSLATION FIX - Translating message:', messageId, 'to', targetLanguage);
      
      const response = await axios.post(`${getApiUrl()}/translate-message`, {
        message_id: messageId,
        target_language: targetLanguage
      });
      
      // Update the message with translation
      setMessages(prev => prev.map(msg => 
        msg.id === messageId 
          ? { 
              ...msg, 
              translated_text: response.data.translated_text,
              has_translation: true,
              _translation_permanent: true
            }
          : msg
      ));
      
      // Auto-expand the translation (using separate state that survives refresh)
      setExpandedTranslations(prev => new Set([...prev, messageId]));
      
    } catch (error) {
      console.error('Translation error:', error);
      alert(t('chat.translateError') + ': ' + (error.response?.data?.detail || error.message));
    }
  };

  const renderMessage = (message) => {
    const isOwnMessage = message.sender_id === currentUser.id;
    const isProjectOwner = project?.creator_id === currentUser.id;
    // Kann bearbeiten/löschen: eigene Nachrichten ODER Projekt-Inhaber
    const canEditMessage = isOwnMessage || isProjectOwner;
    
    // Debug log for image messages
    if (message.message_type === 'image') {
      console.log('Rendering image message:', {
        id: message.id,
        file_path: message.file_path,
        full_url: getFullFileUrl(message.file_path),
        message_type: message.message_type
      });
    }
    
    return (
      <div 
        key={message.id} 
        data-message-id={message.id}
        className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} group`}
      >
        <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg relative ${
          isOwnMessage 
            ? 'bg-emerald-500 text-white' 
            : 'bg-white text-gray-800 shadow-sm'
        }`}>
          {/* Reply indicator */}
          {message.reply_to && (
            <div className="mb-2 p-2 bg-black bg-opacity-10 rounded text-xs">
              <p className="font-medium">Antwort auf:</p>
              <p className="truncate">{message.reply_to_text}</p>
            </div>
          )}
          
          <p className="text-sm font-medium mb-1">
            {isOwnMessage ? 'Du' : (projectMembers[message.sender_id] || 'Teammitglied_FIRST')}
          </p>
          
          {/* Render file content */}
          {message.message_type === 'image' && message.file_path && (
            <div className="mb-2 cursor-pointer" onClick={() => onFilePreview && onFilePreview(getFullFileUrl(message.file_path), message.original_filename, 'image')}>
              <img 
                src={getFullFileUrl(message.file_path)} 
                alt={message.original_filename || 'Uploaded image'}
                className="max-w-full h-auto rounded border hover:opacity-80 transition-opacity"
                style={{ maxHeight: '200px', maxWidth: '250px' }}
                onLoad={() => console.log('Image loaded successfully:', message.file_path)}
                onError={(e) => {
                  console.error('Image load error:', message.file_path, e);
                  e.target.style.display = 'block';
                  e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"><rect width="100" height="100" fill="%23f0f0f0"/><text x="50" y="50" text-anchor="middle" dy=".3em" fill="%23999">Bild nicht gefunden</text></svg>';
                }}
              />
              <p className="text-xs mt-1 opacity-75">{message.original_filename}</p>
              <DebugUrlDisplay url={getFullFileUrl(message.file_path)} label="Bild-URL" />
            </div>
          )}

          {/* DXF CAD Files */}
          {message.message_type === 'dxf' && message.file_path && (
            <div className="mb-2">
              <div className="flex items-center p-3 bg-emerald-50 border border-emerald-200 rounded">
                <div className="w-8 h-8 text-emerald-600 mr-3 flex items-center justify-center">
                  <Ruler className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-emerald-800">CAD-Zeichnung (DXF)</p>
                  <p className="text-sm text-emerald-600">{message.original_filename}</p>
                  <DebugUrlDisplay url={getFullFileUrl(message.file_path)} label="DXF-URL" />
                </div>
                {/* Mobile: Direct download button, Desktop: Preview */}
                {isMobile ? (
                  <a
                    href={getFullFileUrl(message.file_path)}
                    download={message.original_filename}
                    className="ml-2 p-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    title={t('files.downloadDxf', 'DXF herunterladen')}
                  >
                    ⬇️
                  </a>
                ) : (
                  <button
                    onClick={() => onFilePreview && onFilePreview(getFullFileUrl(message.file_path), message.original_filename, 'dxf')}
                    className="ml-2 p-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                    title="CAD-Zeichnung anzeigen"
                  >
                    <Ruler className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* PDF Files */}
          {message.message_type === 'pdf' && message.file_path && (
            <div className="mb-2">
              <div className="flex items-center p-3 bg-red-50 border border-red-200 rounded">
                <FileText className="w-8 h-8 text-red-600 mr-3" />
                <div className="flex-1">
                  <p className="font-medium text-red-800">PDF Dokument</p>
                  <p className="text-sm text-red-600">{message.original_filename}</p>
                  <DebugUrlDisplay url={getFullFileUrl(message.file_path)} label="PDF-URL" />
                </div>
                {/* Mobile: Direkt im Browser öffnen, Desktop: Preview */}
                {isMobile ? (
                  <button
                    onClick={async () => {
                      console.log('📱 MOBILE: Direct PDF blob loading');
                      try {
                        // PDF als Blob laden für direktes Anzeigen
                        const response = await fetch(getFullFileUrl(message.file_path));
                        const blob = await response.blob();
                        const blobUrl = URL.createObjectURL(blob);
                        
                        // Direktes Navigieren zur PDF-URL (kein neues Fenster)
                        window.location.href = blobUrl;
                      } catch (error) {
                        console.error('PDF loading failed:', error);
                        // Fallback zu normalem Link
                        window.open(getFullFileUrl(message.file_path), '_blank');
                      }
                    }}
                    className="ml-2 p-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    title={t('files.openPdf', 'PDF öffnen')}
                  >
                    📄
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      console.log('🖥️ DESKTOP: PDF preview clicked');
                      onFilePreview && onFilePreview(getFullFileUrl(message.file_path), message.original_filename, 'pdf');
                    }}
                    className="ml-2 p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    title={t('files.viewPdf', 'PDF anzeigen')}
                  >
                    👁️
                  </button>
                )}
              </div>
            </div>
          )}

          {/* DOCX/DOC Files */}
          {/* Word Documents */}
          {message.message_type === 'document' && message.file_path && (
            <div 
              className="mb-2 cursor-pointer" 
              onClick={() => {
                if (message.preview_file_path) {
                  onFilePreview && onFilePreview(
                    getFullFileUrl(message.preview_file_path), 
                    message.original_filename, 
                    'pdf',
                    getFullFileUrl(message.file_path)
                  );
                } else {
                  onFilePreview && onFilePreview(getFullFileUrl(message.file_path), message.original_filename, 'document');
                }
              }}
            >
              <div className="flex items-center p-3 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 transition-colors">
                <FileText className="w-8 h-8 text-blue-600 mr-3" />
                <div>
                  <p className="font-medium text-blue-800">
                    Word Dokument
                    {message.preview_file_path && (
                      <span className="ml-2 text-xs text-emerald-600">✓ PDF-Vorschau</span>
                    )}
                  </p>
                  <p className="text-sm text-blue-600">{message.original_filename}</p>
                  <DebugUrlDisplay url={getFullFileUrl(message.file_path)} label="DOCX-URL" />
                  {message.preview_file_path && <DebugUrlDisplay url={getFullFileUrl(message.preview_file_path)} label="Preview-URL" />}
                </div>
              </div>
            </div>
          )}

          {/* Excel Spreadsheets */}
          {message.message_type === 'spreadsheet' && message.file_path && (
            <div 
              className="mb-2 cursor-pointer" 
              onClick={() => {
                if (message.preview_file_path) {
                  onFilePreview && onFilePreview(
                    getFullFileUrl(message.preview_file_path), 
                    message.original_filename, 
                    'pdf',
                    getFullFileUrl(message.file_path)
                  );
                } else {
                  onFilePreview && onFilePreview(getFullFileUrl(message.file_path), message.original_filename, 'spreadsheet');
                }
              }}
            >
              <div className="flex items-center p-3 bg-green-50 border border-green-200 rounded hover:bg-green-100 transition-colors">
                <FileText className="w-8 h-8 text-green-600 mr-3" />
                <div>
                  <p className="font-medium text-green-800">
                    Excel Tabelle
                    {message.preview_file_path && (
                      <span className="ml-2 text-xs text-emerald-600">✓ PDF-Vorschau</span>
                    )}
                  </p>
                  <p className="text-sm text-green-600">{message.original_filename}</p>
                  <DebugUrlDisplay url={getFullFileUrl(message.file_path)} label="XLSX-URL" />
                  {message.preview_file_path && <DebugUrlDisplay url={getFullFileUrl(message.preview_file_path)} label="Preview-URL" />}
                </div>
              </div>
            </div>
          )}

          {/* PowerPoint Presentations */}
          {message.message_type === 'presentation' && message.file_path && (
            <div 
              className="mb-2 cursor-pointer" 
              onClick={() => {
                if (message.preview_file_path) {
                  onFilePreview && onFilePreview(
                    getFullFileUrl(message.preview_file_path), 
                    message.original_filename, 
                    'pdf',
                    getFullFileUrl(message.file_path)
                  );
                } else {
                  onFilePreview && onFilePreview(getFullFileUrl(message.file_path), message.original_filename, 'presentation');
                }
              }}
            >
              <div className="flex items-center p-3 bg-orange-50 border border-orange-200 rounded hover:bg-orange-100 transition-colors">
                <FileText className="w-8 h-8 text-orange-600 mr-3" />
                <div>
                  <p className="font-medium text-orange-800">
                    PowerPoint Präsentation
                    {message.preview_file_path && (
                      <span className="ml-2 text-xs text-emerald-600">✓ PDF-Vorschau</span>
                    )}
                  </p>
                  <p className="text-sm text-orange-600">{message.original_filename}</p>
                </div>
              </div>
            </div>
          )}
          
          {(message.message_type === 'file' || message.message_type === 'voice') && message.file_path && (
            <>
              {/* Audio Player for Voice Messages */}
              {(message.original_filename?.includes('voice_message') || 
                message.original_text?.includes('🎤') ||
                message.file_path?.match(/\.(webm|mp4|wav|m4a|ogg|mp3)$/i)) ? (
                <div className="mb-2 p-3 bg-black bg-opacity-10 rounded">
                  <div className="flex items-center mb-2">
                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 017 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">Sprachnachricht</p>
                      <p className="text-xs opacity-75">
                        {message.file_size ? `${Math.round(message.file_size / 1024)} KB` : ''} • Klicken zum Abspielen
                      </p>
                    </div>
                  </div>
                  
                  {/* Show transcript if available */}
                  {message.original_text && message.original_text !== '🎤 Sprachnachricht' && (
                    <div className="mb-2 p-2 bg-white bg-opacity-50 rounded text-xs">
                      <p className="font-medium mb-1">📝 Transkript:</p>
                      <p className="italic">{message.original_text.replace('🎤 ', '')}</p>
                    </div>
                  )}
                  
                  <audio 
                    src={getFullFileUrl(message.file_path)}
                    controls
                    className="w-full h-8"
                    style={{ 
                      backgroundColor: 'rgba(0,0,0,0.1)', 
                      borderRadius: '4px',
                      outline: 'none'
                    }}
                    preload="auto"
                    onError={(e) => {
                      console.error('Audio loading error:', e.target.error);
                      console.error('Full audio URL:', getFullFileUrl(message.file_path));
                    }}
                  >
                    Ihr Browser unterstützt keine Audio-Wiedergabe.
                  </audio>
                </div>
              ) : (
                /* Regular File Display */
                <div className="mb-2 p-2 bg-black bg-opacity-10 rounded flex items-center">
                  <FileText className="w-4 h-4 mr-2" />
                  <div 
                    className="flex-1 cursor-pointer hover:text-emerald-600 transition-colors"
                    onClick={() => {
                      // Detect file type from extension for proper preview
                      const ext = message.original_filename?.split('.').pop()?.toLowerCase();
                      let fileType = null;
                      if (ext === 'txt') fileType = 'text';
                      onFilePreview && onFilePreview(getFullFileUrl(message.file_path), message.original_filename, fileType);
                    }}
                  >
                    <p className="text-sm font-medium">{message.original_filename}</p>
                    <p className="text-xs opacity-75">
                      {message.file_size ? `${Math.round(message.file_size / 1024)} KB` : ''} • Klicken für Vorschau
                    </p>
                  </div>
                  <a 
                    href={getFullFileUrl(message.file_path)} 
                    download={message.original_filename}
                    className="ml-2 p-1 hover:bg-black hover:bg-opacity-10 rounded"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Download className="w-4 h-4" />
                  </a>
                </div>
              )}
            </>
          )}
          
          {/* Text content - ALWAYS show original first, translation on demand */}
          {message.original_text && (
            <div>
              {/* Always show original text first */}
              <p className="whitespace-pre-wrap">{message.original_text}</p>
              
              {/* Show sender language if different from viewer */}
              {message.sender_language && message.sender_language !== currentUser?.language && (
                <p className="text-xs opacity-50 mt-1">
                  ({message.sender_language.toUpperCase()})
                </p>
              )}
              
              {/* Show translation below if available and expanded */}
              {message.has_translation && message.translated_text && expandedTranslations.has(message.id) && (
                <div className="mt-2 pt-2 border-t border-current border-opacity-20">
                  <p className="text-xs opacity-60 mb-1">🌐 {t('chat.translation')} ({currentUser?.language?.toUpperCase() || 'EN'}):</p>
                  <p className="whitespace-pre-wrap">{message.translated_text}</p>
                </div>
              )}
            </div>
          )}
          
          {/* Translation button - show if sender language differs from viewer's language */}
          {message.sender_language && 
           message.sender_language !== currentUser?.language && 
           message.original_text && (
            <Button
              size="sm"
              variant="ghost"
              className="mt-2 text-xs opacity-60 hover:opacity-100"
              onClick={() => {
                if (message.has_translation && message.translated_text) {
                  // Toggle translation visibility using separate state (survives auto-refresh)
                  setExpandedTranslations(prev => {
                    const newSet = new Set(prev);
                    if (newSet.has(message.id)) {
                      newSet.delete(message.id);
                    } else {
                      newSet.add(message.id);
                    }
                    return newSet;
                  });
                } else {
                  // Request translation and auto-expand
                  translateMessage(message.id, currentUser.language);
                  setExpandedTranslations(prev => new Set([...prev, message.id]));
                }
              }}
            >
              {expandedTranslations.has(message.id) ? `🔽 ${t('chat.hideTranslation')}` : `🌐 ${t('chat.translate')}`}
            </Button>
          )}
          
          <p className="text-xs opacity-70 mt-1">
            {formatMessageTime(message.timestamp)}
            {message.edited && <span className="ml-2 italic">({t('chat.edited')})</span>}
          </p>
          
          {/* WhatsApp-like action menu */}
          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="flex gap-1">
              <Button
                size="sm"
                variant="ghost"
                className="h-6 w-6 p-0 hover:bg-black hover:bg-opacity-20"
                onClick={() => handleReplyToMessage(message)}
                title="Antworten"
              >
                <ArrowLeft className="w-3 h-3" />
              </Button>
              {canEditMessage && (
                <>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 w-6 p-0 hover:bg-black hover:bg-opacity-20"
                    onClick={() => handleEditMessage(message)}
                    title={t('common.edit', 'Edit')}
                  >
                    <Settings className="w-3 h-3" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 w-6 p-0 hover:bg-red-500"
                    onClick={() => handleDeleteMessage(message.id)}
                    title={t('common.delete', 'Löschen')}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Messages Area - Scrollable (OBEN) */}
      <div 
        ref={chatContainerRef} 
        className="flex-1 overflow-y-auto bg-gray-50 chat-scrollbar"
        style={{ minHeight: 0, padding: isMobile ? '8px' : '16px' }}
      >
        <div className="space-y-3">
          {/* TIMELINE STYLE: NEWEST FIRST (neueste oben, ältere unten) */}
          {messages.map(renderMessage)}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Voice Recording Display - über dem Input */}
      {isRecording && (
        <div className="mx-2 mb-1 p-2 bg-blue-50 border border-blue-200 rounded-lg shadow-sm flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
              <span className="text-sm font-semibold text-blue-700">🎤 {recordingTime}s</span>
            </div>
            <button
              onClick={stopVoiceRecording}
              className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-xs transition-colors"
            >
              ⏹️ Stoppen
            </button>
          </div>
        </div>
      )}

      {/* Transcription Loading Indicator - über dem Input */}
      {isTranscribing && !isRecording && (
        <div className="mx-2 mb-1 p-3 bg-amber-50 border border-amber-200 rounded-lg shadow-sm flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
              <div className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
              <div className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
            </div>
            <span className="text-sm font-medium text-amber-700">
              {t('chat.transcribing', 'Sprachaufnahme wird transkribiert...')}
            </span>
          </div>
          <p className="text-xs text-amber-600 mt-1">
            {t('chat.transcribingHint', 'Dies kann bei längeren Aufnahmen bis zu 30-40 Sekunden dauern.')}
          </p>
        </div>
      )}

      {/* Chat Input Field - UNTEN (wie WhatsApp/Telegram) */}
      <div 
        className="bg-white border-t flex-shrink-0"
        style={{ 
          padding: isMobile ? '6px' : '12px',
          paddingBottom: isMobile ? '10px' : '12px'
        }}
      >
        <div style={{
          display: 'flex', 
          gap: isMobile ? '6px' : '8px', 
          alignItems: 'center', 
          width: '100%'
        }}>
          {/* Photo Camera Button - Mobile Only */}
          {(() => {
            const checkMobile = () => {
              return window.innerWidth <= 768 || /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
            };
            
            return checkMobile() && (
              <>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '8px',
                    backgroundColor: uploading ? '#9ca3af' : '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    cursor: 'pointer',
                    width: '40px',
                    height: '40px',
                    flexShrink: 0,
                    fontSize: '18px',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    touchAction: 'manipulation',
                    WebkitTapHighlightColor: 'transparent'
                  }}
                  htmlFor="camera-file-input"
                >
                  {uploading ? '⏳' : '📷'}
                </label>
                <input
                  id="camera-file-input"
                  type="file"
                  onChange={handlePhotoCapture}
                  style={{ display: 'none' }}
                  accept="image/*"
                  capture="environment"
                  disabled={uploading || isRecording}
                />
              </>
            );
          })()}

          {/* Text Input */}
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder={t('chat.typeMessage')}
            onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
            style={{
              flex: '1',
              padding: isMobile ? '10px 14px' : '10px 12px',
              border: '1px solid #e5e7eb',
              borderRadius: '24px',
              fontSize: '16px',
              backgroundColor: '#f3f4f6',
              color: 'black',
              minWidth: '0',
              outline: 'none'
            }}
          />

          {/* Voice Recording Button */}
          <button
            type="button"
            disabled={uploading}
            onClick={isRecording ? stopVoiceRecording : startVoiceRecording}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '8px',
              backgroundColor: isRecording ? '#ef4444' : '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              cursor: 'pointer',
              width: '40px',
              height: '40px',
              flexShrink: 0,
              fontSize: '18px'
            }}
          >
            {isRecording ? '🔴' : '🎤'}
          </button>

          {/* Senden Button */}
          <button
            onClick={sendMessage}
            disabled={!newMessage.trim() || uploading}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '8px',
              backgroundColor: newMessage.trim() ? '#059669' : '#9ca3af',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              cursor: 'pointer',
              width: '40px',
              height: '40px',
              flexShrink: 0,
              fontSize: '18px'
            }}
          >
            ➤
          </button>
        </div>
      </div>

        {/* Photo Caption Modal */}
        {pendingPhoto && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-4">
              <h3 className="text-lg font-semibold mb-3">{t('chat.sendPhotoWithCaption', 'Send photo with caption')}</h3>
              
              {/* Photo Preview */}
              <div className="mb-4">
                <img 
                  src={pendingPhoto.preview} 
                  alt="Foto Vorschau" 
                  className="w-full h-48 object-cover rounded border"
                />
              </div>
              
              {/* Caption Input */}
              <div className="mb-4">
                <Input
                  placeholder={t('chat.addCaptionOptional', 'Add caption (optional)...')}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      uploadPhotoWithCaption(e.target.value);
                    }
                  }}
                  autoFocus
                />
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-2 justify-end">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    if (pendingPhoto.preview) {
                      URL.revokeObjectURL(pendingPhoto.preview);
                    }
                    setPendingPhoto(null);
                  }}
                  disabled={uploading}
                >
                  {t('common.cancel', 'Cancel')}
                </Button>
                <Button 
                  onClick={(e) => {
                    const captionInput = e.target.closest('.bg-white').querySelector('input');
                    uploadPhotoWithCaption(captionInput?.value || '');
                  }}
                  disabled={uploading}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {uploading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : null}
                  {t('chat.send')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Transcript Review Modal */}
        {pendingTranscript && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-lg w-full p-4">
              <h3 className="text-lg font-semibold mb-3">📝 {t('chat.reviewTranscript', 'Review transcript')}</h3>
              
              {pendingTranscript.error && (
                <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
                  {pendingTranscript.error}
                </div>
              )}
              
              <p className="text-sm text-gray-600 mb-3">
                {t('chat.reviewTranscriptDesc', 'Please review and correct the transcribed text if needed:')}
              </p>
              
              {/* Editable Transcript */}
              <div className="mb-4">
                <textarea
                  id="transcript-textarea"
                  className="w-full p-3 border rounded-lg resize-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  rows={6}
                  defaultValue={pendingTranscript.text}
                  placeholder={t('chat.enterOrCorrectText', 'Enter or correct text here...')}
                  autoFocus
                />
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-2 justify-end">
                <Button 
                  variant="outline" 
                  onClick={cancelTranscript}
                >
                  {t('common.cancel', 'Cancel')}
                </Button>
                <Button 
                  onClick={() => {
                    const textarea = document.getElementById('transcript-textarea');
                    saveReviewedTranscript(textarea?.value || '');
                  }}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  ✓ {t('common.save', 'Save')}
                </Button>
              </div>
            </div>
          </div>
        )}

    </div>
  );
};

export default Chat;
